import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft, FileSignature, ClipboardCheck,
} from "lucide-react";
import { toast } from "sonner";
import { logWarn } from "@/lib/log";
import SignaturePad from "@/components/SignaturePad";
import ConsentDialog from "@/components/ConsentDialog";
import { DownloadManualButton } from "@/components/DownloadManualButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _normalizeRegion = (s) =>
  (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().trim();

const DNI_REGEX = /^([0-9]{8}[A-Z]|[XYZ][0-9]{7}[A-Z])$/;

export default function StudentScreen() {
  const navigate = useNavigate();
  // Course context comes from the PIN entered on the access screen
  const courseId = sessionStorage.getItem("course_id") || "";
  const courseName = sessionStorage.getItem("course_name") || "";
  const regionName = sessionStorage.getItem("region_name") || "";
  const courseType = sessionStorage.getItem("course_type") || "";
  const isPpaa = courseType.toLowerCase() === "ppaa";
  const session2Open = (() => {
    try { return JSON.parse(sessionStorage.getItem("session2_open") || "false"); }
    catch { return false; }
  })();
  // Sesión de firma que toca HOY: 2 (día 2) o 3 (día 3 · cursos de MURCIA).
  const signSession = (() => {
    const n = parseInt(sessionStorage.getItem("sign_session") || "", 10);
    if (n === 2 || n === 3) return n;
    return session2Open ? 2 : null;
  })();
  const examAvailable = (() => {
    // 2026-10: no hay restricción horaria. El examen está disponible desde
    // que el instructor pulsa "ACCESO A EXAMEN" en su panel.
    try { return JSON.parse(sessionStorage.getItem("exam_available") || "false"); }
    catch { return false; }
  })();

  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [dni, setDni] = useState("");
  const [profession, setProfession] = useState("");
  const [prefillNotice, setPrefillNotice] = useState(null);
  const [showSignature, setShowSignature] = useState(false);
  const [pendingSignature, setPendingSignature] = useState(null);
  const [showConsent, setShowConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  // Estados específicos para la firma de la 2ª sesión (cursos INICIAL):
  // muestra un SignaturePad dedicado y envía a /api/student/sign-session2.
  const [showSession2Pad, setShowSession2Pad] = useState(false);
  const [session2Saving, setSession2Saving] = useState(false);
  // 2026-06-10: pantalla de resumen "ASISTENCIA CONFIRMADA" tras firmar.
  // { signature, session: 1 | 2 } | null
  const [confirmation, setConfirmation] = useState(null);

  // Memo to avoid re-creating inline objects on every render (would force
  // <ConsentDialog /> to re-render and run its effects again unnecessarily).
  const courseObj = useMemo(() => {
    let info = {};
    try {
      info = JSON.parse(sessionStorage.getItem("course_info") || "{}") || {};
    } catch {
      info = {};
    }
    return { ...info, id: courseId, name: courseName, region_name: regionName };
  }, [courseId, courseName, regionName]);
  const studentObj = useMemo(
    () => ({ name, surname, dni }),
    [name, surname, dni]
  );

  const isCastillaLeon = _normalizeRegion(regionName) === "CASTILLA Y LEON";

  useEffect(() => {
    if (!courseId) {
      navigate("/");
    }
  }, [courseId, navigate]);

  // Pre-fill student data from past attendances or pre-registered list.
  // El DNI es el dato único del alumno (2026-10).
  const runPrefill = async (params) => {
    if (!params.dni) return;
    try {
      const res = await axios.get(`${API}/student/prefill`, {
        params: { ...params, course_id: courseId },
      });
      if (res.data?.found) {
        if (res.data.name) setName(res.data.name);
        if (res.data.surname) setSurname(res.data.surname);
        if (res.data.dni) setDni(res.data.dni);
        if (res.data.profession) setProfession(res.data.profession);
        setPrefillNotice("Hemos encontrado tus datos. Compruébalos y edita lo que necesites.");
      }
    } catch (e) {
      logWarn("Prefill lookup failed", e);
    }
  };

  // Evita repetir la misma consulta (se dispara al escribir y al salir del campo)
  const lastLookupRef = useRef("");

  const prefillByDni = (rawDni) => {
    const dniNorm = (rawDni || "").trim().toUpperCase();
    if (!DNI_REGEX.test(dniNorm)) return;
    if (lastLookupRef.current === `d:${dniNorm}`) return;
    lastLookupRef.current = `d:${dniNorm}`;
    runPrefill({ dni: dniNorm });
  };

  const validateForm = () => {
    if (!name.trim() || !surname.trim() || !dni.trim()) {
      toast.error("Rellena nombre, apellidos y DNI.");
      return false;
    }
    const dniRegex = /^[0-9]{8}[A-Za-z]$|^[XYZxyz][0-9]{7}[A-Za-z]$/;
    if (!dniRegex.test(dni.trim())) {
      toast.error("DNI no válido. Formato: 12345678A");
      return false;
    }
    if (isCastillaLeon && !profession.trim()) {
      toast.error("La profesión es obligatoria para cursos en Castilla y León.");
      return false;
    }
    return true;
  };

  const handleConfirmAttendance = () => {
    if (!validateForm()) return;
    setShowSignature(true);
  };

  const handleExam = () => {
    if (!validateForm()) return;
    if (!examAvailable) {
      toast.error("El examen aún no está disponible. Pregunta a tu instructor.");
      return;
    }
    navigate("/exam", {
      state: {
        name: name.trim(),
        surname: surname.trim(),
        dni: dni.trim().toUpperCase(),
        course_id: courseId,
        course_name: courseName,
      },
    });
  };

  const handleSaveSignature = (signatureBase64) => {
    setPendingSignature(signatureBase64);
    setShowSignature(false);
    setShowConsent(true);
  };

  const handleConsentConfirm = async ({ consent_accepted, image_consent_denied }) => {
    if (!pendingSignature) {
      toast.error("Datos incompletos.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/attendance`, {
        name: name.trim(),
        surname: surname.trim(),
        dni: dni.trim().toUpperCase(),
        profession: isCastillaLeon ? profession.trim() : "",
        course_id: courseId,
        course_name: courseName,
        signature_base64: pendingSignature,
        consent_accepted,
        image_consent_denied,
      });
      setShowConsent(false);
      setConfirmation({ signature: pendingSignature, session: 1 });
      setPendingSignature(null);
    } catch (e) {
      const msg = e.response?.data?.detail || "Error al guardar la firma.";
      toast.error(typeof msg === "string" ? msg : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  // Firma de la 2ª sesión (solo INICIAL). Requiere el DNI para localizar
  // la asistencia previa.
  const handleSession2Save = async (signature_base64) => {
    if (!dni) {
      toast.error("Introduce tu DNI para identificar tu asistencia.");
      setShowSession2Pad(false);
      return;
    }
    setSession2Saving(true);
    try {
      await axios.post(`${API}/student/sign-session2`, {
        course_id: courseId,
        dni: (dni || "").trim().toUpperCase(),
        signature_base64,
        session: signSession || 2,
      });
      setShowSession2Pad(false);
      setConfirmation({ signature: signature_base64, session: signSession || 2 });
    } catch (e) {
      const msg = e.response?.data?.detail || "No se pudo registrar la firma.";
      toast.error(typeof msg === "string" ? msg : "Error al firmar la 2ª sesión");
    } finally {
      setSession2Saving(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#FAFAFA]" data-testid="student-screen">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-950 transition-colors"
            data-testid="back-to-pin-button"
          >
            <ChevronLeft className="h-4 w-4" />
            Volver
          </button>
          <div
            className="text-xs uppercase tracking-[0.18em] text-zinc-500 truncate max-w-[60%]"
            data-testid="active-course-name"
            title={courseName}
          >
            {courseName || "Curso"}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-16">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500 mb-3">
            <span className="inline-block h-2 w-2 bg-black" />
            Registro de Alumno
          </div>
          <h1 className="heading-font text-4xl sm:text-5xl font-extrabold text-zinc-950 leading-[0.95]">
            {isPpaa ? "Asistencia" : "Asistencia y exámenes"}
          </h1>
          <p className="mt-3 text-zinc-600">
            Completa tus datos y confirma tu asistencia con tu firma.
          </p>
        </div>

        <div className="bg-white border border-zinc-200 rounded-lg p-6 md:p-10 space-y-6">
          {prefillNotice && (
            <div
              className="rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 px-4 py-3 text-sm"
              data-testid="prefill-notice"
            >
              {prefillNotice}
            </div>
          )}

          <div
            className="rounded-md border border-zinc-200 bg-zinc-50 p-4 md:p-5"
            data-testid="student-identify-block"
          >
            <div className="text-xs uppercase tracking-[0.16em] text-zinc-500 mb-4">
              Identifícate con tu <span className="font-bold text-zinc-800">DNI</span>
            </div>
            <div className="grid grid-cols-1 gap-5">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  DNI <span className="text-red-600">*</span>
                </label>
                <Input
                  value={dni}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase().slice(0, 9);
                    setDni(v);
                    prefillByDni(v);
                  }}
                  onBlur={(e) => prefillByDni(e.target.value)}
                  placeholder="12345678A"
                  maxLength={9}
                  className="touch-input border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0 tracking-wider bg-white"
                  data-testid="student-dni-input"
                  required
                  autoFocus
                />
              </div>
            </div>
            <p className="mt-3 text-xs text-zinc-500">
              Con tu DNI recuperamos el resto de tus datos si ya has hecho
              algún curso con nosotros.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Nombre <span className="text-red-600">*</span>
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value.toUpperCase())}
                placeholder="Tu nombre"
                className="touch-input border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                data-testid="student-name-input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Apellidos <span className="text-red-600">*</span>
              </label>
              <Input
                value={surname}
                onChange={(e) => setSurname(e.target.value.toUpperCase())}
                placeholder="Tus apellidos"
                className="touch-input border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                data-testid="student-surname-input"
                required
              />
            </div>
          </div>

          {isCastillaLeon && (
            <div data-testid="profession-section">
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Profesión <span className="text-red-600">*</span>
                <span className="ml-2 text-xs text-zinc-500 font-normal">
                  (obligatorio en Castilla y León)
                </span>
              </label>
              <Input
                value={profession}
                onChange={(e) => setProfession(e.target.value.toUpperCase())}
                placeholder="Tu profesión"
                className="touch-input border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                data-testid="student-profession-input"
              />
            </div>
          )}

          <div className={`grid grid-cols-1 gap-4 pt-2 ${isPpaa ? "" : "md:grid-cols-2"}`}>
            <Button
              onClick={handleConfirmAttendance}
              disabled={saving}
              className="h-14 text-base font-semibold bg-zinc-950 hover:bg-zinc-800 text-white rounded-md transition-colors"
              data-testid="confirm-attendance-button"
            >
              <FileSignature className="mr-2 h-5 w-5" />
              Confirmar asistencia
            </Button>
            {!isPpaa && (
            <Button
              onClick={handleExam}
              variant="outline"
              className={`h-14 text-base font-semibold rounded-md transition-colors border-2 ${
                examAvailable
                  ? "border-zinc-900 text-zinc-900 hover:bg-zinc-100"
                  : "border-red-500 text-red-600 hover:bg-red-50"
              }`}
              data-testid="take-exam-button"
            >
              <ClipboardCheck className="mr-2 h-5 w-5" />
              Hacer examen
            </Button>
            )}
          </div>

          <div className="pt-1">
            <DownloadManualButton
              testid="sign-download-manual"
              className="h-12 w-full md:w-auto"
            />
          </div>

          {signSession && ["inicial", "ppaa"].includes((courseType || "").toLowerCase()) && (
            <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-4 space-y-3">
              <div className="text-sm font-semibold text-amber-900">
                Firma de la {signSession}ª sesión
              </div>
              <p className="text-xs text-amber-800">
                Estás en el día {signSession} del curso. Pulsa el botón para
                registrar tu firma de hoy.
              </p>
              <Button
                onClick={() => {
                  if (!dni) {
                    toast.error("Introduce tu DNI.");
                    return;
                  }
                  setShowSession2Pad(true);
                }}
                disabled={session2Saving}
                className="h-12 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-md"
                data-testid={`open-session${signSession}-signature`}
              >
                <FileSignature className="mr-2 h-5 w-5" />
                Firmar {signSession}ª sesión
              </Button>
            </div>
          )}
        </div>
      </main>

      {showSignature && (
        <SignaturePad
          onCancel={() => setShowSignature(false)}
          onSave={handleSaveSignature}
          saving={false}
          courseName={courseName}
          studentName={`${name} ${surname}`.trim()}
        />
      )}

      {showSession2Pad && (
        <SignaturePad
          onCancel={() => setShowSession2Pad(false)}
          onSave={handleSession2Save}
          saving={session2Saving}
          courseName={`${courseName} · ${signSession || 2}ª sesión`}
          studentName={`${name} ${surname}`.trim()}
        />
      )}

      <ConsentDialog
        open={showConsent}
        onOpenChange={(o) => {
          setShowConsent(o);
          if (!o) setPendingSignature(null);
        }}
        course={courseObj}
        student={studentObj}
        onConfirm={handleConsentConfirm}
        submitting={saving}
      />

      {/* Pantalla de resumen tras firmar (sesion 1 o 2) */}
      {confirmation && (
        <div
          className="fixed inset-0 z-50 bg-[#FAFAFA] overflow-y-auto"
          data-testid="attendance-confirmation-screen"
        >
          <div className="max-w-2xl mx-auto px-6 md:px-10 py-10 md:py-16">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-100 mb-4">
                <ClipboardCheck className="h-8 w-8 text-emerald-700" />
              </div>
              <div className="text-xs uppercase tracking-[0.22em] text-emerald-700 font-bold mb-2">
                {confirmation.session >= 2
                  ? `Firma ${confirmation.session}ª sesión`
                  : "Registro completo"}
              </div>
              <h1
                className="heading-font text-3xl sm:text-5xl font-extrabold text-zinc-950 leading-[0.95]"
                data-testid="confirmation-title"
              >
                ASISTENCIA CONFIRMADA
              </h1>
              <p className="mt-3 text-zinc-600">
                Estos son los datos que has enviado. Guarda este resumen si necesitas justificarlo.
              </p>
            </div>

            <div className="bg-white border-2 border-zinc-200 rounded-lg p-6 md:p-8 space-y-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Curso</div>
                <div className="text-base font-semibold text-zinc-950 mt-1">
                  {courseName || "—"}
                </div>
                {regionName && (
                  <div className="text-xs text-zinc-500">{regionName}</div>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Nombre</div>
                  <div className="text-base font-semibold text-zinc-950 mt-1">{name || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Apellidos</div>
                  <div className="text-base font-semibold text-zinc-950 mt-1">{surname || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">DNI</div>
                  <div className="text-base font-mono font-semibold text-zinc-950 mt-1 tracking-wider">
                    {(dni || "").toUpperCase() || "—"}
                  </div>
                </div>
                {isCastillaLeon && profession && (
                  <div className="sm:col-span-2">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Profesión</div>
                    <div className="text-base font-semibold text-zinc-950 mt-1">{profession}</div>
                  </div>
                )}
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2">
                  Firma {confirmation.session >= 2 ? `(${confirmation.session}ª sesión)` : ""}
                </div>
                <div className="bg-zinc-50 border-2 border-zinc-200 rounded-md p-3">
                  <img
                    src={confirmation.signature}
                    alt="Firma del alumno"
                    className="w-full h-auto max-h-56 object-contain"
                    data-testid="confirmation-signature-image"
                  />
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-3">
              <DownloadManualButton
                testid="confirmation-download-manual"
                className="h-14 px-6 text-base"
              />
              <Button
                onClick={() => {
                  setConfirmation(null);
                  navigate("/");
                }}
                className="h-14 px-10 bg-zinc-950 hover:bg-zinc-800 text-white text-base font-bold tracking-wide rounded-md"
                data-testid="confirmation-accept-btn"
              >
                ACEPTAR
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
